/* global $ */
import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "../components/Footer";

function AddVendor() {

    const navigate = useNavigate();
    const location = useLocation();

    const isEdit = location.state?.isEdit || false;
    const existingVendor = location.state?.vendor || null;

    const [isSaving, setIsSaving] = useState(false);
    const [loadingData, setLoadingData] = useState(isEdit);

    const [vendorData, setVendorData] = useState({
        name: "",
        email: "",
        phone: "",
        alternate_phone: "",
        website: "",
        gst_number: "",
        pan_number: "",
        address_line1: "",
        address_line2: "",
        country_id: "",
        state_id: "",
        city_id: "",
        pincode: "",
        latitude: "",
        longitude: "",
        notes: ""
    });

    const [contacts, setContacts] = useState([
        { name: "", email: "", phone: "", designation: "", notes: "" }
    ]);
    const [selectedServiceTypes, setSelectedServiceTypes] = useState([""]);
    const [existingContactIds, setExistingContactIds] = useState([]);
    const [existingServiceTypeRecords, setExistingServiceTypeRecords] = useState([]);

    const [countries, setCountries] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [allCities, setAllCities] = useState([]);
    const [filteredStates, setFilteredStates] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [serviceTypesLoaded, setServiceTypesLoaded] = useState(false);
    const [pendingSTIds, setPendingSTIds] = useState([]);

    const [errors, setErrors] = useState({});

    // ─── Load Dropdowns ───────────────────────────────────────────────────────────

    useEffect(() => {
        loadDropdowns();
    }, []);

    const loadDropdowns = async () => {
        const safe = async (url, setter, label) => {
            try {
                const res = await axiosInstance.get(url);
                const raw = res.data;
                const list = Array.isArray(raw) ? raw
                    : Array.isArray(raw?.data) ? raw.data
                    : Array.isArray(raw?.rows) ? raw.rows
                    : Array.isArray(raw?.result) ? raw.result
                    : [];
                console.log(`✅ ${label}: ${list.length} items`);
                setter(list);
            } catch (err) {
                console.error(`❌ ${label} FAILED:`, err?.response?.status, url);
                setter([]);
            }
        };

        await Promise.all([
            safe('/api/country',      setCountries,    'Countries'),
            safe('/api/state',        setAllStates,    'States'),
            safe('/api/cities',       setAllCities,    'Cities'),
            safe('/api/serviceType',  setServiceTypes, 'ServiceTypes'),
        ]);
        setServiceTypesLoaded(true);
    };

    // ─── Cascading: Country → State ───────────────────────────────────────────────

    useEffect(() => {
        if (vendorData.country_id) {
            const filtered = allStates.filter(
                s => String(s.country_id) === String(vendorData.country_id)
            );
            setFilteredStates(filtered);
            // Check if current state_id still valid
            const stateStillValid = filtered.some(
                s => String(s.id) === String(vendorData.state_id)
            );
            if (!stateStillValid) {
                setVendorData(prev => ({ ...prev, state_id: '', city_id: '' }));
                setFilteredCities([]);
            }
        } else {
            setFilteredStates([]);
            setFilteredCities([]);
        }
    }, [vendorData.country_id, allStates]);

    // ─── Cascading: State → City ──────────────────────────────────────────────────

    useEffect(() => {
        if (vendorData.state_id) {
            const filtered = allCities.filter(
                c => String(c.state_id) === String(vendorData.state_id)
            );
            setFilteredCities(filtered);
            // Check if current city_id still valid
            const cityStillValid = filtered.some(
                c => String(c.id) === String(vendorData.city_id)
            );
            if (!cityStillValid) {
                setVendorData(prev => ({ ...prev, city_id: '' }));
            }
        } else {
            setFilteredCities([]);
        }
    }, [vendorData.state_id, allCities]);

    // ─── Re-apply pending service type IDs after dropdown loads ──────────────────

    useEffect(() => {
        if (serviceTypesLoaded && pendingSTIds.length > 0) {
            setSelectedServiceTypes(pendingSTIds);
            setPendingSTIds([]);
        }
    }, [serviceTypesLoaded, pendingSTIds]);

    // ─── Prefill for Edit ─────────────────────────────────────────────────────────

    useEffect(() => {
        if (isEdit && existingVendor) {
            prefillVendorData();
        }
    }, [isEdit]);

    const prefillVendorData = async () => {
        setLoadingData(true);
        try {
            setVendorData({
                name: existingVendor.name || "",
                email: existingVendor.email || "",
                phone: existingVendor.phone || "",
                alternate_phone: existingVendor.alternate_phone || "",
                website: existingVendor.website || "",
                gst_number: existingVendor.gst_number || "",
                pan_number: existingVendor.pan_number || "",
                address_line1: existingVendor.address_line1 || "",
                address_line2: existingVendor.address_line2 || "",
                country_id: existingVendor.country_id || "",
                state_id: existingVendor.state_id || "",
                city_id: existingVendor.city_id || "",
                pincode: existingVendor.pincode || "",
                latitude: existingVendor.latitude ?? "",
                longitude: existingVendor.longitude ?? "",
                notes: existingVendor.notes || ""
            });

            // Load existing contacts
            try {
                const contactRes = await axiosInstance.get(`/api/vendorsContact?vendor_id=${existingVendor.id}`);
                const existingContacts = contactRes.data.data || contactRes.data || [];
                if (existingContacts.length > 0) {
                    setContacts(existingContacts.map(c => ({
                        id: c.id,
                        name: c.name || "",
                        email: c.email || "",
                        phone: c.phone || "",
                        designation: c.designation || "",
                        notes: c.notes || ""
                    })));
                    setExistingContactIds(existingContacts.map(c => c.id));
                }
            } catch (err) {
                console.log("Could not load contacts:", err);
            }

            // Load existing service types
            try {
                const stRes = await axiosInstance.get(`/api/vendorServiceType?vendor_id=${existingVendor.id}`);
                const existingSTs = stRes.data.data || stRes.data || [];
                if (existingSTs.length > 0) {
                    const mappedIds = existingSTs.map(st =>
                        String(st.service_type_id || st.service_type?.id || "")
                    );
                    setExistingServiceTypeRecords(existingSTs);
                    if (serviceTypesLoaded) {
                        setSelectedServiceTypes(mappedIds);
                    } else {
                        setPendingSTIds(mappedIds);
                    }
                }
            } catch (err) {
                console.log("Could not load service types:", err);
            }

        } finally {
            setLoadingData(false);
        }
    };

    // ─── Field Handlers ───────────────────────────────────────────────────────────

    const handleChange = (e) => {
        const { name, value } = e.target;
        setVendorData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    // ─── Contact Handlers ─────────────────────────────────────────────────────────

    const handleContactChange = (index, e) => {
        const updated = [...contacts];
        updated[index][e.target.name] = e.target.value;
        setContacts(updated);
    };

    const addContact = () => {
        setContacts([...contacts, { name: "", email: "", phone: "", designation: "", notes: "" }]);
    };

    const removeContact = (index) => {
        if (contacts.length === 1) return;
        setContacts(contacts.filter((_, i) => i !== index));
    };

    // ─── Service Type Handlers ────────────────────────────────────────────────────

    const handleServiceTypeChange = (index, value) => {
        const updated = [...selectedServiceTypes];
        updated[index] = value;
        setSelectedServiceTypes(updated);
    };

    const addServiceType = () => {
        setSelectedServiceTypes([...selectedServiceTypes, ""]);
    };

    const removeServiceType = (index) => {
        if (selectedServiceTypes.length === 1) return;
        setSelectedServiceTypes(selectedServiceTypes.filter((_, i) => i !== index));
    };

    // ─── Validation — only name, phone, country are required ─────────────────────

    const validateForm = () => {
        let formErrors = {};
        if (!vendorData.name) formErrors.name = "Vendor name is required";
        if (!vendorData.phone) formErrors.phone = "Phone is required";
        if (!vendorData.country_id) formErrors.country_id = "Country is required";
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return false;
        }
        return true;
    };

    // ─── Save ─────────────────────────────────────────────────────────────────────

    const saveVendor = async (e) => {
        e.preventDefault();
        if (isSaving) return;
        if (!validateForm()) return;

        setIsSaving(true);

        try {
            const user_id = localStorage.getItem("user_id");
            const toNull = (val) => (val === "" || val === undefined ? null : val);

            const vendorPayload = {
                ...vendorData,
                latitude: toNull(vendorData.latitude),
                longitude: toNull(vendorData.longitude),
                country_id: toNull(vendorData.country_id),
                state_id: toNull(vendorData.state_id),
                city_id: toNull(vendorData.city_id),
                updated_by: user_id
            };

            let vendor_id;

            if (isEdit) {
                await axiosInstance.put(`/api/vendors/${existingVendor.id}`, vendorPayload);
                vendor_id = existingVendor.id;

                const validContacts = contacts.filter(c => c.name || c.phone);
                for (const contact of validContacts) {
                    if (contact.id) {
                        await axiosInstance.put(`/api/vendorsContact/${contact.id}`, {
                            name: contact.name, email: contact.email, phone: contact.phone,
                            designation: contact.designation, notes: contact.notes, updated_by: user_id
                        });
                    } else {
                        await axiosInstance.post("/api/vendorsContact", {
                            ...contact, vendor_id, isactive: true, created_by: user_id
                        });
                    }
                }

                const currentContactIds = validContacts.filter(c => c.id).map(c => c.id);
                const removedIds = existingContactIds.filter(id => !currentContactIds.includes(id));
                for (const cid of removedIds) {
                    await axiosInstance.delete(`/api/vendorsContact/${cid}`);
                }

                const validSTs = selectedServiceTypes.filter(st => st);
                const existingSTIds = existingServiceTypeRecords.map(st => st.service_type_id);
                const addedSTs   = validSTs.filter(st => !existingSTIds.includes(st));
                const removedSTs = existingServiceTypeRecords.filter(st => !validSTs.includes(st.service_type_id));

                for (const stRecord of removedSTs) {
                    await axiosInstance.delete(`/api/vendorServiceType/${stRecord.id}`);
                }
                for (const service_type_id of addedSTs) {
                    await axiosInstance.post("/api/vendorServiceType", { vendor_id, service_type_id, created_by: user_id });
                }

                Swal.fire({ icon: "success", title: "Updated!", text: "Vendor updated successfully", timer: 1500, showConfirmButton: false });

            } else {
                vendorPayload.created_by = user_id;
                vendorPayload.isactive = true;
                vendorPayload.status = "active";

                const vendorRes = await axiosInstance.post("/api/vendors", vendorPayload);
                vendor_id = vendorRes.data?.data?.id || vendorRes.data?.id;

                const validContacts = contacts.filter(c => c.name || c.phone);
                if (validContacts.length > 0) {
                    await Promise.all(validContacts.map(contact =>
                        axiosInstance.post("/api/vendorsContact", { ...contact, vendor_id, isactive: true, created_by: user_id })
                    ));
                }

                const validSTs = selectedServiceTypes.filter(st => st);
                if (validSTs.length > 0) {
                    await Promise.all(validSTs.map(service_type_id =>
                        axiosInstance.post("/api/vendorServiceType", { vendor_id, service_type_id, created_by: user_id })
                    ));
                }

                Swal.fire({ icon: "success", title: "Saved!", text: "Vendor created successfully", timer: 1500, showConfirmButton: false });
            }

            navigate(`/vendor-details/${vendor_id}`);

        } catch (error) {
            Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Failed to save vendor" });
        }

        setIsSaving(false);
    };

    // ─── Loading ──────────────────────────────────────────────────────────────────

    if (loadingData) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="card mb-0">
                        <div className="card-body">

                            <div className="page-header">
                                <div className="content-page-header d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <button type="button" className="btn btn-sm btn-outline-secondary me-3"
                                            onClick={() => isEdit
                                                ? navigate(`/vendor-details/${existingVendor.id}`)
                                                : navigate('/vendor')
                                            }>
                                            <i className="fa fa-arrow-left me-1"></i> Back
                                        </button>
                                        <h5 className="mb-0">{isEdit ? "Edit Vendor" : "Add Vendor"}</h5>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={saveVendor}>

                                {/* ── Basic Details ── */}
                                <h5 className="form-title">Basic Details</h5>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label>Vendor Name <span className="text-danger">*</span></label>
                                        <input type="text" className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            name="name" value={vendorData.name} onChange={handleChange} />
                                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Email</label>
                                        <input type="email" className="form-control"
                                            name="email" value={vendorData.email} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Phone <span className="text-danger">*</span></label>
                                        <input type="text" className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                            name="phone" value={vendorData.phone} onChange={handleChange} />
                                        {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Alternate Phone</label>
                                        <input type="text" className="form-control"
                                            name="alternate_phone" value={vendorData.alternate_phone} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Website</label>
                                        <input type="text" className="form-control"
                                            name="website" value={vendorData.website} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>GST Number</label>
                                        <input type="text" className="form-control"
                                            name="gst_number" value={vendorData.gst_number} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>PAN Number</label>
                                        <input type="text" className="form-control"
                                            name="pan_number" value={vendorData.pan_number} onChange={handleChange} />
                                    </div>
                                </div>

                                {/* ── Address ── */}
                                <h5 className="form-title mt-4">Address</h5>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label>Address Line 1</label>
                                        <textarea className="form-control" name="address_line1"
                                            value={vendorData.address_line1} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label>Address Line 2</label>
                                        <textarea className="form-control" name="address_line2"
                                            value={vendorData.address_line2} onChange={handleChange} />
                                    </div>

                                    {/* Country — mandatory */}
                                    <div className="col-md-4 mb-3">
                                        <label>Country <span className="text-danger">*</span></label>
                                        <select className={`form-control ${errors.country_id ? 'is-invalid' : ''}`}
                                            name="country_id" value={vendorData.country_id} onChange={handleChange}>
                                            <option value="">Select Country</option>
                                            {countries.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                        {errors.country_id && <div className="invalid-feedback">{errors.country_id}</div>}
                                    </div>

                                    {/* State — optional, cascades from country */}
                                    <div className="col-md-4 mb-3">
                                        <label>State</label>
                                        <select className="form-control" name="state_id"
                                            value={vendorData.state_id} onChange={handleChange}
                                            disabled={!vendorData.country_id}>
                                            <option value="">
                                                {vendorData.country_id ? 'Select State' : 'Select Country First'}
                                            </option>
                                            {filteredStates.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* City — optional, cascades from state */}
                                    <div className="col-md-4 mb-3">
                                        <label>City</label>
                                        <select className="form-control" name="city_id"
                                            value={vendorData.city_id} onChange={handleChange}
                                            disabled={!vendorData.state_id}>
                                            <option value="">
                                                {vendorData.state_id ? 'Select City' : 'Select State First'}
                                            </option>
                                            {filteredCities.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label>Pincode</label>
                                        <input type="text" className="form-control"
                                            name="pincode" value={vendorData.pincode} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Latitude</label>
                                        <input type="text" className="form-control"
                                            name="latitude" value={vendorData.latitude} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Longitude</label>
                                        <input type="text" className="form-control"
                                            name="longitude" value={vendorData.longitude} onChange={handleChange} />
                                    </div>
                                </div>

                                {/* ── Vendor Contacts ── */}
                                <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
                                    <h5 className="form-title mb-0">Vendor Contacts</h5>
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addContact}>
                                        + Add Contact
                                    </button>
                                </div>

                                {contacts.map((contact, index) => (
                                    <div key={index} className="border rounded p-3 mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <small className="text-muted fw-semibold">
                                                Contact {index + 1}
                                                {contact.id && (
                                                    <span className="badge bg-info ms-2" style={{ fontSize: '10px' }}>Existing</span>
                                                )}
                                            </small>
                                            {contacts.length > 1 && (
                                                <button type="button" className="btn btn-sm btn-outline-danger"
                                                    onClick={() => removeContact(index)}>
                                                    &times; Remove
                                                </button>
                                            )}
                                        </div>
                                        <div className="row">
                                            <div className="col-md-3 mb-2">
                                                <label>Name</label>
                                                <input type="text" className="form-control" name="name"
                                                    value={contact.name} onChange={(e) => handleContactChange(index, e)} />
                                            </div>
                                            <div className="col-md-3 mb-2">
                                                <label>Email</label>
                                                <input type="email" className="form-control" name="email"
                                                    value={contact.email} onChange={(e) => handleContactChange(index, e)} />
                                            </div>
                                            <div className="col-md-3 mb-2">
                                                <label>Phone</label>
                                                <input type="text" className="form-control" name="phone"
                                                    value={contact.phone} onChange={(e) => handleContactChange(index, e)} />
                                            </div>
                                            <div className="col-md-3 mb-2">
                                                <label>Designation</label>
                                                <input type="text" className="form-control" name="designation"
                                                    value={contact.designation} onChange={(e) => handleContactChange(index, e)} />
                                            </div>
                                            <div className="col-md-12 mt-2">
                                                <label>Notes</label>
                                                <textarea className="form-control" name="notes" rows={2}
                                                    value={contact.notes} onChange={(e) => handleContactChange(index, e)} />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* ── Service Types ── */}
                                <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
                                    <h5 className="form-title mb-0">Vendor Service Types</h5>
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addServiceType}>
                                        + Add Service Type
                                    </button>
                                </div>

                                {!serviceTypesLoaded ? (
                                    <div className="text-muted small py-2">
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Loading service types...
                                    </div>
                                ) : (
                                    selectedServiceTypes.map((st, index) => (
                                        <div key={index} className="row align-items-center mb-2">
                                            <div className="col-md-4">
                                                <select className="form-control" value={String(st)}
                                                    onChange={(e) => handleServiceTypeChange(index, e.target.value)}>
                                                    <option value="">Select Service Type</option>
                                                    {serviceTypes.map(s => (
                                                        <option key={s.id} value={String(s.id)}>{s.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            {selectedServiceTypes.length > 1 && (
                                                <div className="col-md-2">
                                                    <button type="button" className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeServiceType(index)}>
                                                        &times; Remove
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}

                                {/* ── Notes ── */}
                                <h5 className="form-title mt-4">Notes</h5>
                                <div className="mb-3">
                                    <textarea className="form-control" name="notes" rows="3"
                                        value={vendorData.notes} onChange={handleChange}
                                        placeholder="Any additional notes about this vendor..." />
                                </div>

                                {/* ── Submit ── */}
                                <div className="text-end mt-4">
                                    <button type="button" className="btn btn-secondary me-2"
                                        onClick={() => isEdit
                                            ? navigate(`/vendor-details/${existingVendor.id}`)
                                            : navigate('/vendor')
                                        }>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={isSaving}>
                                        {isSaving
                                            ? (isEdit ? "Updating..." : "Saving...")
                                            : (isEdit ? "Update Vendor" : "Save Vendor")
                                        }
                                    </button>
                                </div>

                            </form>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
}

export default AddVendor;